import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD, NAV } from "@/constants/testIds";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Building2, Calendar, ShoppingBag, Settings as SettingsIcon,
  CreditCard, LogOut, QrCode, Sparkles, Landmark, Users, Trophy,
} from "lucide-react";

const navItems = [
  { to: "/dashboard/venue-info", label: "Venue info", icon: Building2, tid: "dash-nav-venue-info" },
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, exact: true, tid: DASHBOARD.navOverview },
  { to: "/dashboard/events", label: "Events", icon: Calendar, tid: DASHBOARD.navEvents },
  { to: "/dashboard/scanner", label: "Scanner", icon: QrCode, tid: "dash-nav-scanner" },
  { to: "/dashboard/orders", label: "Orders", icon: ShoppingBag, tid: DASHBOARD.navOrders },
  { to: "/dashboard/members", label: "Members", icon: Users, tid: "dash-nav-members" },
  { to: "/dashboard/payouts", label: "Payouts", icon: Landmark, tid: "dash-nav-payouts" },
  { to: "/dashboard/dividends", label: "Dividends", icon: Trophy, tid: "dash-nav-dividends" },
  { to: "/dashboard/settings", label: "Branding", icon: SettingsIcon, tid: DASHBOARD.navSettings },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard, tid: DASHBOARD.navBilling },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-zinc-50 text-zinc-900" data-testid={DASHBOARD.root}>
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-zinc-200 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-6 py-4 flex items-center border-b border-zinc-200">
          <Link to="/" className="flex items-center" data-testid={NAV.logo}>
            <Logo size="h-24" />
          </Link>
        </div>
        <nav className="p-4 flex-1 space-y-1">
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.exact}
                data-testid={it.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {it.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-zinc-200">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="dash-admin-link"
              className="mb-3 flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-500 transition"
            >
              <Sparkles className="w-4 h-4" /> Admin pipeline
            </Link>
          )}
          <div className="text-xs text-zinc-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid={NAV.logoutBtn}
            className="mt-3 w-full justify-start px-3 h-9 text-sm text-zinc-700"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
